/**
 * bc-how-it-works.js — Clair
 * ─────────────────────────────────────────────────────────────────────────────
 * Custom element <bc-animated-steps> that auto-cycles through .bc-hiw__step
 * children, adding .bc-hiw__step--active to the current step.
 *
 * Attributes:
 *   interval  — cycle duration in milliseconds (default: 3000)
 *               Changing this attribute at runtime restarts the timer.
 *
 * Behaviour:
 *   - Adds .bc-hiw--js to the closest .bc-hiw ancestor so CSS can swap from the
 *     no-JS fallback to the JS-driven active state.
 *   - Pauses on mouseenter / focusin, resumes on mouseleave / focusout.
 *     Pause adds .bc-hiw--paused so the CSS progress bar stops in sync.
 *   - Skips cycling when prefers-reduced-motion is set; marks step 0 active.
 *   - Cleans up the timer on disconnectedCallback.
 * ─────────────────────────────────────────────────────────────────────────────
 */

if (!customElements.get('bc-animated-steps')) {
class BcAnimatedSteps extends HTMLElement {
  // ── Observed attributes ──────────────────────────────────────────────────
  static get observedAttributes() {
    return ['interval'];
  }

  constructor() {
    super();
    /** @type {HTMLElement[]} */
    this._steps = [];
    this._current = 0;
    /** @type {number|null} */
    this._timer = null;
    this._reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Stable bound references for add/removeEventListener.
    this._onPause  = () => this._pause();
    this._onResume = () => this._resume();
  }

  // ── Lifecycle ────────────────────────────────────────────────────────────

  connectedCallback() {
    this._steps = Array.from(this.querySelectorAll('.bc-hiw__step'));
    if (this._steps.length === 0) return;

    // Signal to CSS that the component is active.
    this._root?.classList.add('bc-hiw--js');

    if (this._steps.length === 1 || this._reducedMotion) {
      this._setActive(0);
      return;
    }

    this.addEventListener('mouseenter', this._onPause);
    this.addEventListener('focusin',    this._onPause);
    this.addEventListener('mouseleave', this._onResume);
    this.addEventListener('focusout',   this._onResume);

    this._setActive(0);
    this._start();
  }

  disconnectedCallback() {
    this._stop();
    this._root?.classList.remove('bc-hiw--js', 'bc-hiw--paused');
    this.removeEventListener('mouseenter', this._onPause);
    this.removeEventListener('focusin',    this._onPause);
    this.removeEventListener('mouseleave', this._onResume);
    this.removeEventListener('focusout',   this._onResume);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'interval' && oldValue !== null && oldValue !== newValue) {
      this._stop();
      this._setActive(this._current);
      this._start();
    }
  }

  // ── Private ──────────────────────────────────────────────────────────────

  get _root() {
    return this.closest('.bc-hiw');
  }

  get _interval() {
    return parseInt(this.getAttribute('interval') || '3000', 10);
  }

  /**
   * Activate a step by index.
   * Removes the class first and forces a reflow so the CSS progress-line
   * animation restarts cleanly.
   *
   * @param {number} index
   */
  _setActive(index) {
    const ACTIVE = 'bc-hiw__step--active';

    this._steps.forEach((step, i) => {
      if (i === index) {
        step.classList.remove(ACTIVE);
        void step.offsetWidth; // force reflow → restart CSS animation
        step.classList.add(ACTIVE);
      } else {
        step.classList.remove(ACTIVE);
      }
    });

    this._current = index;
  }

  _advance() {
    this._setActive((this._current + 1) % this._steps.length);
  }

  _pause() {
    this._stop();
    this._root?.classList.add('bc-hiw--paused');
  }

  _resume() {
    this._root?.classList.remove('bc-hiw--paused');
    this._setActive(this._current);
    this._start();
  }

  _start() {
    if (this._timer !== null) return;
    this._timer = window.setInterval(() => this._advance(), this._interval);
  }

  _stop() {
    if (this._timer === null) return;
    window.clearInterval(this._timer);
    this._timer = null;
  }
}

customElements.define('bc-animated-steps', BcAnimatedSteps);
}
